import React from 'react';
import { Link } from 'react-router-dom';
import { Dumbbell, Music, Trees, Waves, Trophy, Film, Palette, PartyPopper, ArrowRight, Check, DollarSign } from 'lucide-react';

const spaceCategories = [
  { name: 'Home Gym', icon: Dumbbell, image: 'https://images.unsplash.com/photo-1540497077202-7c8a3999166f?w=800&q=80' },
  { name: 'Studio', icon: Music, image: 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=800&q=80' },
  { name: 'Backyard', icon: Trees, image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80' },
  { name: 'Pool', icon: Waves, image: 'https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?w=800&q=80' },
  { name: 'Tennis Court', icon: Trophy, image: 'https://images.unsplash.com/photo-1554068865-24cecd4e34b8?w=800&q=80' },
  { name: 'Home Theater', icon: Film, image: 'https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=800&q=80' },
  { name: 'Event Space', icon: PartyPopper, image: 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=800&q=80' },
];

const earningsExamples = [
  { space: 'Home Gym', range: '$300 – $1,500/month', icon: Dumbbell },
  { space: 'Studio', range: '$200 – $1,200/month', icon: Music },
  { space: 'Backyard', range: '$200 – $2,000/month', icon: Trees },
];

export default function HostLandingPage() {
  return (
    <div className="min-h-screen bg-[#050505] font-sans">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1920&q=80" 
            alt="Modern luxury home" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/90 to-[#050505]/40" />
        </div>
        
        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-24">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37] mb-4">For Hosts</p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-white leading-[1.1] mb-6">
              Turn Your Unused Space Into <span className="text-[#D4AF37]">Extra Income</span>
            </h1>
            <p className="text-lg md:text-xl font-light text-zinc-300 leading-relaxed mb-8">
              List your home gym, studio, backyard, or private space and earn money on your schedule.
            </p>
            
            <Link 
              to="/become-host" 
              data-testid="host-hero-cta-btn"
              className="bg-[#D4AF37] text-[#050505] font-medium px-8 py-4 rounded-full transition-all hover:bg-[#E5C564] hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] inline-flex items-center gap-2"
            >
              Become a Host
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* How Hosting Works */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37] mb-4">The Process</p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light text-white">
              How Hosting Works
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'List your space', desc: 'Add photos, set your pricing, and describe what makes your space unique' },
              { step: '02', title: 'Approve booking requests', desc: 'Review guest requests and approve the ones that work for you' },
              { step: '03', title: 'Earn extra income', desc: 'Get paid securely after each booking is completed' },
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-[#141414] border border-[#D4AF37]/15 rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 hover:border-[#D4AF37]/40"
              >
                <span className="text-6xl font-serif font-light text-[#D4AF37]/30">{item.step}</span>
                <h3 className="font-serif text-xl md:text-2xl text-white mt-4 mb-3">{item.title}</h3>
                <p className="text-zinc-400 font-light">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits for Hosts */}
      <section className="py-24 md:py-32 border-t border-[#D4AF37]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Why Host</p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light text-white mb-12">
              Benefits for Hosts
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
              {[
                'Earn from unused space',
                'Set your availability',
                'Stay in control',
                'No long-term commitments',
                'Flexible income',
              ].map((benefit, index) => (
                <div key={index} className="flex items-center gap-3 text-zinc-300 bg-[#0A0A0A] p-4 rounded-xl border border-[#D4AF37]/10">
                  <Check size={20} className="text-[#D4AF37] flex-shrink-0" />
                  <span>{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Examples of Spaces */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Space Types</p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light text-white">
              Examples of Spaces
            </h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {spaceCategories.map((category, index) => (
              <div 
                key={index}
                className="group relative aspect-square rounded-2xl overflow-hidden"
              >
                <img 
                  src={category.image} 
                  alt={category.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
                <div className="absolute inset-0 border border-[#D4AF37]/0 group-hover:border-[#D4AF37]/40 rounded-2xl transition-all duration-300" />
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex items-center gap-2">
                    <category.icon size={18} className="text-[#D4AF37]" />
                    <span className="text-white font-medium">{category.name}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Earnings Potential */}
      <section className="py-24 md:py-32 border-t border-[#D4AF37]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Potential</p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light text-white">
              Earnings Potential
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {earningsExamples.map((item, index) => (
              <div 
                key={index}
                className="bg-[#0A0A0A] border border-[#D4AF37]/15 rounded-2xl p-8 text-center transition-all duration-300 hover:-translate-y-1 hover:border-[#D4AF37]/40"
              >
                <item.icon size={40} className="text-[#D4AF37] mx-auto mb-4" />
                <h3 className="font-serif text-xl text-white mb-2">{item.space}</h3>
                <p className="text-2xl font-light text-[#D4AF37]">{item.range}</p>
              </div>
            ))}
          </div>
          
          <p className="text-center text-zinc-500 text-sm mt-8">
            * Earnings vary based on location, space type, pricing, and booking frequency
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <DollarSign size={48} className="text-[#D4AF37] mx-auto mb-6" />
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light text-white mb-4">
            Your Space Is Already Valuable
          </h2>
          <p className="text-lg text-zinc-400 mb-10 max-w-xl mx-auto">
            Start earning from the spaces you already have. Join 100Nook today.
          </p>
          
          <Link 
            to="/become-host" 
            data-testid="host-final-cta-btn"
            className="bg-[#D4AF37] text-[#050505] font-medium px-10 py-5 rounded-full text-lg transition-all hover:bg-[#E5C564] hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] inline-flex items-center gap-2"
          >
            Start Hosting
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#050505] border-t border-[#D4AF37]/10 py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <p className="text-zinc-500 text-sm">© 2026 100Nook. All rights reserved.</p>
            <div className="flex flex-wrap justify-center gap-6 text-sm text-zinc-400">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <Link to="/search" className="hover:text-white transition-colors">Explore Spaces</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
